import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { PartnerStatusDto } from 'views/RevisionHistory/Partner/PartnerRevisionDtos';
import { RevisionDetail, RevisionDetailsError } from 'views/RevisionHistory/RevisionDtos';
import { useFetchPartnerComparison } from 'hooks/queries/useFetchPartnerComparison';
import { Box, Table, TableBody } from '@mui/material';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import { DataTable } from 'components/DataTable/DataTable';
import { revisionHistoryHeadcells } from 'views/RevisionHistory/Partner/RevisionHistoryHeadcells';
import { closeModal, openModal } from 'reducers/modalReducer';
import { JBErrorViewHttpInformation } from '@bjb-gcrm/gcrm-frontend-components';
import { filterAndSortRevisionHistoryEntries } from 'views/RevisionHistory/editHistoryHiddenAttributes';
import { DlmLightRowType } from 'views/DraftMode/DraftSwitch';
import { EDIT_HISTORY_HIDDEN_ATTRIBUTES } from 'views/RevisionHistory/Partner/hidden-attributes';

interface PartnerRevisionHistoryDetailTableProps {
  techId: string;
  partnerStatusDtos: PartnerStatusDto[];
  index: number;
  dlmLightRowType: DlmLightRowType;
}

export function PartnerRevisionHistoryDetailTable({
  partnerStatusDtos,
  index,
  techId,
  dlmLightRowType,
}: PartnerRevisionHistoryDetailTableProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = partnerStatusDtos[index];
  const previousHistory = partnerStatusDtos[index + 1];
  let oldVersion: number | null;
  let newVersion: number | null;

  switch (dlmLightRowType) {
    case 'LATEST_CHANGES_NO_DRAFT':
      // compare the latest received version to null (open changes in DLM light)
      oldVersion = history.version;
      newVersion = null;
      break;
    case 'LATEST_CHANGES_NO_VERSIONS':
      // compare null (empty objects) to null (open changes in DLM light)
      oldVersion = null;
      newVersion = null;
      break;
    case 'LATEST_CHANGES_WITH_DRAFT':
    case 'NORMAL_VERSION':
      // compare previous version to draft version
      oldVersion = previousHistory?.version ?? null;
      newVersion = history.version;
      break;
    default:
      throw new Error(`DLM light row type not configured: ${dlmLightRowType}`);
  }

  const {
    data,
    isFetching,
    isError,
    error: comparisonError,
  } = useFetchPartnerComparison(techId, oldVersion, newVersion);

  const sortedData: RevisionDetail[] = isError
    ? [
        {
          section: 'All sections',
          fieldNameTranslation: '',
          fieldName: '',
          error: {
            message: `Error encountered while fetching comparison between version ${oldVersion} and ${newVersion}: ${comparisonError?.message}`,
            code: comparisonError?.response?.status ?? -1,
            cause: comparisonError?.response,
          } as RevisionDetailsError,
        },
      ]
    : filterAndSortRevisionHistoryEntries(EDIT_HISTORY_HIDDEN_ATTRIBUTES, data);

  const onCloseErrorClick = () => {
    dispatch(closeModal('JBErrorModalWrapper'));
  };

  const onOpenErrorClick = (row: RevisionDetail) => {
    const title = t('revisionHistory.table.error.title');
    const userErrorMessage = t('revisionHistory.table.error.message', { sectionName: row.section });
    const httpInfo: JBErrorViewHttpInformation = {
      ...row.error,
      requestUrl: '',
      requestMethod: 'GET',
      responseStatus: row.error?.code ?? -1,
    };
    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          technicalInfo: { httpInfo, details: row.error?.message, title, userErrorMessage },
          handleClose: onCloseErrorClick,
        },
      }),
    );
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {isFetching ? (
        <Table>
          <TableBody>
            <SkeletonTableRow rowCount={5} cellCount={6} id="detailed-history-skeleton" />
          </TableBody>
        </Table>
      ) : (
        <DataTable
          id="revision-history-table"
          headCells={revisionHistoryHeadcells(onOpenErrorClick)}
          rows={sortedData}
        />
      )}
    </Box>
  );
}
